import React, { useEffect, useMemo, useRef, useState } from "react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FaStore, FaUser, FaMotorcycle, FaMapMarkerAlt } from "react-icons/fa";
import i18n from "../../lib/i18n";
import { MapPopup } from "../../lib/utils/GoogleMapMulti";

export default function OrderDetailsPop({ data, onClose }) {

  const t = i18n.t.bind(i18n);

  const allPhotos = useMemo(() => (data?.items || []).map((it) => it.photo).filter(Boolean), [data]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const hasMultiplePhotos = allPhotos.length > 1;

  useEffect(() => {
    setCurrentIndex(0);
    setLoading(false);
  }, [data]);

  const nextSlide = () => {
    if (!allPhotos.length) return;
    setLoading(true);
    setCurrentIndex((idx) => (idx + 1) % allPhotos.length);
  };

  const prevSlide = () => {
    if (!allPhotos.length) return;
    setLoading(true);
    setCurrentIndex((idx) => (idx === 0 ? allPhotos.length - 1 : idx - 1));
  };

  const handleImageLoad = () => setLoading(false);

  const [mapOpen, setMapOpen] = useState(false);

  const markers = useMemo(() => {
    const result = [];
    if (data?.store) {
      result.push({
        id: "store",
        type: "store",
        name: data.store.name,
        phone: data.store.phone,
        address: data.store.address,
        lat: Number(data.store.latitude),
        lng: Number(data.store.longitude),
      });
    }
    if (data?.user) {
      result.push({
        id: "user",
        type: "user",
        name: data.user.name,
        phone: data.user.phone,
        address: data.user.address,
        lat: Number(data.user.latitude),
        lng: Number(data.user.longitude),
      });
    }
    if (data?.delivery) {
      result.push({
        id: "delivery",
        type: "delivery",
        name: data.delivery.name,
        phone: data.delivery.phone,
        address: "",
        lat: Number(data.delivery.latitude),
        lng: Number(data.delivery.longitude),
      });
    }
    return result.filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lng));
  }, [data]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-thirdly rounded-2xl shadow-lg lg:w-[74%] md:w-[80%] sm:w-[92%] max-w-6xl p-6 relative overflow-y-auto max-h-[86vh]">
        <button onClick={onClose} className="absolute top-3 right-4 text-gray-400 hover:text-red-500 text-xl">
          ✕
        </button>

        <h2 className="text-2xl text-center text-white font-bold mb-4">{data?.id ? `#${data.id} — ${data?.order_status || ""}` : t("order.title")}</h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* LEFT: Image / items summary */}
          <div className="col-span-1 bg-black/20 p-3 rounded-xl">
            <div className="relative flex justify-center items-center w-full h-56 sm:h-64">
              {loading && (
                <div className="absolute z-10 bg-black/60 text-white px-4 py-2 rounded">Loading...</div>
              )}

              {allPhotos.length ? (
                <img
                  key={currentIndex}
                  src={allPhotos[currentIndex]}
                  alt={data?.items?.[currentIndex]?.title || "item"}
                  onLoad={handleImageLoad}
                  className="w-full h-56 object-cover rounded-xl"
                />
              ) : (
                <div className="w-full h-56 flex items-center justify-center rounded-xl bg-black/30 text-white">
                  {t("order.no_image")}
                </div>
              )}

              {hasMultiplePhotos && (
                <>
                  <button onClick={prevSlide} className="absolute top-1/2 -translate-y-1/2 left-0 bg-secondary p-2 rounded-full text-white">
                    <IoIosArrowBack size={10}/>
                  </button>
                  <button onClick={nextSlide} className="absolute top-1/2 -translate-y-1/2 right-0 bg-secondary p-2 rounded-full text-white">
                    <IoIosArrowForward size={10} />
                  </button>
                </>
              )}
            </div>

            <div className="mt-3 text-white">
              <h3 className="text-lg font-semibold">{t("order.items")}</h3>
              <ul className="mt-2 space-y-2">
                {(data?.items || []).map((it) => (
                  <li key={it.id} className="flex items-start gap-3">
                    <img src={it.photo} alt={it.title} className="w-14 h-14 object-cover rounded" />
                    <div>
                      <div className="font-semibold">{it.title}</div>
                      <div className="text-sm text-gray-200">{t("order.qty")}: {it.quantity} • {t("order.price")}: {it.price}</div>
                    </div>
                  </li>
                ))}
              </ul>


            </div>
          </div>

          {/* MIDDLE: Store */}
          <div className="col-span-1 bg-black/20 p-4 rounded-xl flex flex-col gap-3">

            <div className="text-md text-gray-200">
              <div className="mb-7"><span className="font-semibold text-white text-md">{t("order.total")} :</span> {data?.total}</div>

              <div className="mb-3"><span className="font-semibold text-white text-md">{t("order.storeFee")} :</span> {data?.storeFee}</div>
              <div className="mb-3"><span className="font-semibold text-white text-md">{t("order.sub_total_after_discount")} :</span> {data?.sub_total_after_discount}</div>
              <div className="mb-7"><span className="font-semibold text-white text-md">{t("order.profit_company_from_store")} :</span> {data?.profit_company_from_store}</div>

              <div className="mb-3"><span className="font-semibold text-white text-md">{t("order.deliveryFee")} :</span> {data?.deliveryFee}</div>
              <div className="mb-3"><span className="font-semibold text-white text-md">{t("order.profit_delivery")} :</span> {data?.profit_delivery}</div>
              <div className="mb-7"><span className="font-semibold text-white text-md">{t("order.profit_company_from_delivery")} :</span> {data?.profit_company_from_delivery}</div>

              <div className="mb-7"><span className="font-semibold text-white text-md">{t("order.profit_company_from_open_order")} :</span> {data?.profit_company_from_open_order}</div>

              <div className="mb-3 text-gray-300">{t("order.distance")} {data?.distance}</div>
              <div className="mb-3 ">{t("order.time_window")} {data?.start_time_for_delivery} — {data?.end_time_for_delivery}</div>
            </div>
            <div className="mt-auto ">
              <div className="flex justify-start gap-2 items-center mb-5 ">
                <p className="text-white text-md">{t("order.store_status")} : </p>
                <p className={`text-sm  ${data.store_status === "PENDING" || data.store_status === "IN_PROGRESS" ? "text-warning" : data.store_status === "COMPLETED" || data.store_status === "DRIVER_ARRIVED" ? "text-success" :  data.store_status === "CANCELLED"  ? "text-red-600" : data.store_status === "ON_THE_WAY" || data.store_status === "GO_TO_STORE" || data.store_status === "AT_THE_STORE" || data.store_status === "DRIVER_ARRIVED" ? "text-blue-600" : "text-gray-300"}`}> {data?.store_status}</p>
              </div>
              <div className="flex justify-start gap-2 items-center mb-5 ">
                <p className="text-white text-md">{t("order.deliver_status")} : </p>
                <p className={`text-sm  ${data.store_status === "PENDING" || data.store_status === "IN_PROGRESS" ? "text-warning" : data.store_status === "COMPLETED" || data.store_status === "DRIVER_ARRIVED" ? "text-success" :  data.store_status === "CANCELLED"  ? "text-red-600" : data.store_status === "ON_THE_WAY" || data.store_status === "GO_TO_STORE" || data.store_status === "AT_THE_STORE" || data.store_status === "DRIVER_ARRIVED" ? "text-blue-600" : "text-gray-300"}`}> {data?.delivery_status}</p>
              </div>
            </div>


          </div>

          {/* RIGHT: Delivery & User */}
          <div className="col-span-1 bg-black/20 p-4 rounded-xl flex flex-col gap-4">
            <div className="flex items-center gap-3 mb-7">
              <div className="p-3 rounded-lg bg-white/10">
                <FaMotorcycle className="text-2xl text-white" />
              </div>
              <div>
                <div className="font-semibold text-white">{data?.delivery?.name}</div>
                <div className="text-sm text-gray-200">{data?.delivery?.phone}</div>
                <div className="text-sm text-gray-200">{t("order.eta")} {data?.total_time_for_delivery_by_minute} {t("order.minutes")}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-7">
              <div className="p-3 rounded-lg bg-white/10">
                <FaUser className="text-2xl text-white" />
              </div>
              <div>
                <div className="font-semibold text-white">{data?.user?.name}</div>
                <div className="text-sm text-gray-200">{data?.user?.phone}</div>
                <div className="text-sm text-gray-200">{data?.user?.address}</div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-white/10">
                <FaStore className="text-2xl text-white" />
              </div>
              <div>
                <div className="font-semibold text-white">{data?.store?.name}</div>
                <div className="text-sm text-gray-200">{data?.store?.phone}</div>
                <div className="text-sm text-gray-200">{data?.store?.address}</div>
              </div>
            </div>

            <div className="flex items-center justify-center mt-10">
              <button onClick={() => setMapOpen(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-white">
                <FaMapMarkerAlt /> {t("order.view_on_map")}
              </button>
            </div>


          </div>
        </div>

        {/* Map popup component (modal) */}
        {mapOpen && <MapPopup markers={markers} onClose={() => setMapOpen(false)} />}
      </div>
    </div>
  );
}
